import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IeltsAttemptContext } from '../utils/ieltsStart';
import { Base_URL } from '../constants/api';
import {
  buildIeltsDraftKey,
  loadIeltsDraftFromBrowser,
  removeIeltsDraftFromBrowser,
  saveIeltsDraftToBrowser,
} from '../utils/ieltsDraftStorage';

const BACKEND_SAVE_INTERVAL_MS = 5000;

function isBackendDraftNewer(backendDraft, localDraft) {
  if (!backendDraft?.updated_at) return false;
  if (!localDraft?.updatedAt) return true;
  return new Date(backendDraft.updated_at).getTime() > new Date(localDraft.updatedAt).getTime();
}

function mapBackendDraftToState(backendDraft) {
  if (!backendDraft) return null;
  return {
    answers: backendDraft.answers_json || {},
    currentStep: backendDraft.current_step || {},
    timeState: backendDraft.time_state_json || {},
    audioState: backendDraft.audio_state_json || {},
    updatedAt: backendDraft.updated_at,
  };
}

export default function useIeltsAutosave({ enabled = true, restoreEnabled = true, identity, draftState, onRestore }) {
  const attempt = useContext(IeltsAttemptContext);
  const draftId = attempt?.draftId;
  const [hydrated, setHydrated] = useState(false);
  const latestPayloadRef = useRef(null);
  const closedDraftKeyRef = useRef(null);
  const backendTimerRef = useRef(null);
  const lastBackendSaveAtRef = useRef(0);
  const mountedRef = useRef(true);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  const scope = identity?.scope || (identity?.fullTestAttemptId ? 'full' : 'sectional');
  const section = identity?.section;
  const testId = identity?.testId;
  const groupId = identity?.groupId;
  const fullTestAttemptId = identity?.fullTestAttemptId;
  const browserDraftKey = useMemo(() => buildIeltsDraftKey({
    scope,
    section,
    testId,
    groupId,
    fullTestAttemptId,
  }), [fullTestAttemptId, groupId, scope, section, testId]);
  const draftKey = attempt?.draftKey || browserDraftKey;
  const active = Boolean(enabled && draftKey && testId && section);

  const buildPayload = useCallback((state) => ({
    draftKey,
    ...(draftId ? { draftId } : {}),
    scope,
    section,
    testId,
    groupId: groupId || null,
    fullTestAttemptId: fullTestAttemptId || null,
    answers: state?.answers ?? {},
    currentStep: state?.currentStep ?? {},
    timeState: state?.timeState ?? {},
    audioState: state?.audioState ?? {},
    updatedAt: new Date().toISOString(),
  }), [draftKey, draftId, fullTestAttemptId, groupId, scope, section, testId]);

  const flushBackendSave = useCallback(async (requireSuccess = false) => {
    backendTimerRef.current = null;
    const payload = latestPayloadRef.current;
    if (!active || !payload || closedDraftKeyRef.current === payload.draftKey) return;

    lastBackendSaveAtRef.current = Date.now();
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/drafts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data?.responseCode !== 200) throw new Error(data?.message || 'Unable to save progress. Please retry.');
    } catch (error) {
      if (requireSuccess) throw error;
      // Browser draft remains available. The next state change will retry backend autosave.
    }
  }, [active]);

  const queueBackendSave = useCallback(() => {
    if (!active || backendTimerRef.current) return;
    const elapsed = Date.now() - lastBackendSaveAtRef.current;
    const delay = Math.max(0, BACKEND_SAVE_INTERVAL_MS - elapsed);
    backendTimerRef.current = setTimeout(flushBackendSave, delay);
  }, [active, flushBackendSave]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (backendTimerRef.current) clearTimeout(backendTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function restoreDraft() {
      setHydrated(false);
      if (!active) {
        setHydrated(true);
        return;
      }

      const localDraft = loadIeltsDraftFromBrowser(browserDraftKey);
      if (localDraft?.state && restoreEnabled) {
        onRestoreRef.current?.(localDraft.updatedAt
          ? { ...localDraft.state, updatedAt: localDraft.updatedAt }
          : localDraft.state);
      }

      try {
        const params = new URLSearchParams({
          draftKey,
          scope,
          section,
          testId: String(testId),
        });
        if (groupId) params.set('groupId', String(groupId));
        if (draftId) params.set('draftId', String(draftId));
        if (fullTestAttemptId) params.set('fullTestAttemptId', String(fullTestAttemptId));

        const response = await fetch(`${Base_URL}/app/users/ielts-tests/drafts?${params.toString()}`, {
          credentials: 'include',
        });
        const data = await response.json();
        const backendDraft = data?.response;
        if (!cancelled && closedDraftKeyRef.current !== draftKey && backendDraft && isBackendDraftNewer(backendDraft, localDraft) && restoreEnabled) {
          onRestoreRef.current?.(mapBackendDraftToState(backendDraft));
        }
      } catch (_) {
        // Local restore already ran. Backend restore will be retried on the next visit.
      } finally {
        if (!cancelled && mountedRef.current) setHydrated(true);
      }
    }

    restoreDraft();
    return () => {
      cancelled = true;
    };
  }, [active, draftKey, browserDraftKey, draftId, fullTestAttemptId, groupId, restoreEnabled, scope, section, testId]);

  useEffect(() => {
    if (!active || !hydrated || !draftState || closedDraftKeyRef.current === draftKey) return;
    const payload = buildPayload(draftState);
    latestPayloadRef.current = payload;
    saveIeltsDraftToBrowser(browserDraftKey, { draftId, draftKey, state: draftState, updatedAt: payload.updatedAt });
    queueBackendSave();
  }, [active, hydrated, draftKey, browserDraftKey, draftId, draftState, buildPayload, queueBackendSave]);

  const clearDraft = useCallback(async () => {
    if (!draftKey) return;
    closedDraftKeyRef.current = draftKey;
    removeIeltsDraftFromBrowser(browserDraftKey);
    latestPayloadRef.current = null;
    if (backendTimerRef.current) {
      clearTimeout(backendTimerRef.current);
      backendTimerRef.current = null;
    }
    if (!active) return;
    try {
      await fetch(`${Base_URL}/app/users/ielts-tests/drafts`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draftKey,
          scope,
          section,
          testId,
          groupId: groupId || null,
          fullTestAttemptId: fullTestAttemptId || null,
        }),
      });
    } catch (_) {
      // Backend clear can be retried by final submit cleanup later; local draft is already removed.
    }
  }, [active, draftKey, browserDraftKey, fullTestAttemptId, groupId, scope, section, testId]);

  const saveDraft = useCallback(async (state = draftState) => {
    latestPayloadRef.current = buildPayload(state);
    await flushBackendSave(true);
  }, [buildPayload, draftState, flushBackendSave]);

  return { clearDraft, saveDraft, draftKey, draftId, hydrated };
}
